/**
 * Slide 4 — Conversão em Caixa, Investimento e Risco
 * VERSÃO MINIMALISTA EXECUTIVA | Premium Board-Ready
 * 
 * Estrutura: 4 blocos
 * 1. Título + Subtítulo editorial
 * 2. Waterfall de Caixa (dominante à esquerda)
 * 3. Scorecard de Capital + Gatilhos de Risco (à direita)
 * 4. 3 Prioridades de Gestão na base
 * 
 * Canvas: 1600 x 900 px
 */

import React, { useMemo, useState, useEffect } from 'react';

// ============ DESIGN TOKENS ============
const COLORS = {
  pageBg: '#0A192F',
  contentBg: '#F7F6F2',
  gold: '#C8A13A',
  textPrimary: '#0A192F',
  textSecondary: '#465467',
  textMuted: '#7A8594',
  textWhite: '#FFFFFF',
  positive: '#16A34A',
  negative: '#C41E6D',
  endpoint: '#1E1B4B',
  warning: '#F59E0B',
};

const FONTS = {
  serif: '"Merriweather", Georgia, "Times New Roman", serif',
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

// ============ CSS KEYFRAMES ============
const injectAnimationStyles = () => {
  const styleId = 'slide4-animations';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes slide4FadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slide4FadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slide4ScaleX {
      from { opacity: 0; transform: scaleX(0); }
      to { opacity: 1; transform: scaleX(1); }
    }
  `;
  document.head.appendChild(style);
};

// ============ CASH WATERFALL CHART ============
const CashWaterfallChart = ({ data, height = 320, isAnimated }) => {
  const processedData = useMemo(() => {
    let runningTotal = 0;
    return data.map((item, index) => {
      const isFirst = index === 0;
      const isLast = index === data.length - 1;
      const isSubtotal = item.isSubtotal;
      
      let barStart, barWidth;
      
      if (isFirst || isSubtotal || isLast) {
        barStart = 0;
        barWidth = Math.abs(item.value);
        if (isFirst) runningTotal = item.value;
        if (isSubtotal) runningTotal = item.value;
      } else {
        barStart = runningTotal;
        barWidth = Math.abs(item.value);
        runningTotal += item.value;
      }
      
      return { ...item, barStart, barWidth, isPositive: item.value >= 0, isFirst, isLast };
    });
  }, [data]);

  const maxValue = Math.max(...data.map(d => Math.abs(d.value))) * 1.15;
  const scale = (value) => (value / maxValue) * 65;

  const totalBars = data.length;
  const barHeight = Math.floor((height - 20) / totalBars) - 10;
  const gap = 10;

  return (
    <div style={{ width: '100%', height, position: 'relative', paddingTop: 10 }}>
      {processedData.map((item, index) => {
        let barLeft = item.isFirst || item.isSubtotal || item.isLast ? 0 : scale(item.barStart);
        let barWidth = scale(item.barWidth);
        
        let barColor;
        if (item.isFirst || item.isSubtotal || item.isLast) {
          barColor = COLORS.endpoint;
        } else {
          barColor = item.isPositive ? COLORS.positive : COLORS.negative;
        }
        
        const yPos = index * (barHeight + gap);
        const valueText = `${item.value >= 0 ? '' : ''}USD ${Math.abs(item.value).toFixed(1)}M`;
        const animDelay = `${0.3 + index * 0.1}s`;
        
        return (
          <div key={index} style={{ 
            position: 'absolute', 
            top: yPos, 
            left: 0, 
            right: 0, 
            height: barHeight, 
            display: 'flex', 
            alignItems: 'center',
            opacity: isAnimated ? 1 : 0,
            animation: isAnimated ? `slide4FadeInUp 0.4s ease-out ${animDelay} both` : 'none',
          }}>
            <div style={{ 
              width: 160, 
              textAlign: 'right', 
              paddingRight: 16, 
              fontFamily: FONTS.sans, 
              fontSize: 13, 
              fontWeight: item.isSubtotal || item.isLast ? 700 : 600, 
              color: COLORS.textSecondary 
            }}>
              {item.label}
            </div>
            <div style={{ flex: 1, position: 'relative', height: barHeight }}>
              <div style={{
                position: 'absolute',
                left: `${barLeft}%`,
                top: 0,
                width: `${Math.max(barWidth, 3)}%`,
                height: barHeight,
                backgroundColor: barColor,
                borderRadius: 6,
                transformOrigin: 'left center',
                animation: isAnimated ? `slide4ScaleX 0.35s ease-out ${animDelay} both` : 'none',
              }} />
              <div style={{
                position: 'absolute',
                left: `${barLeft + barWidth + 2}%`,
                top: 0,
                height: barHeight,
                display: 'flex',
                alignItems: 'center',
                paddingLeft: 10,
              }}>
                <span style={{ 
                  fontFamily: FONTS.sans, 
                  fontSize: 13, 
                  fontWeight: 700, 
                  color: item.isFirst || item.isSubtotal || item.isLast ? COLORS.endpoint : (item.isPositive ? COLORS.positive : COLORS.negative),
                  whiteSpace: 'nowrap' 
                }}>
                  {valueText}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============ CAPITAL METRIC CARD ============
const CapitalMetric = ({ label, value, unit, trend, isAnimated, delay }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    opacity: isAnimated ? 1 : 0,
    animation: isAnimated ? `slide4FadeIn 0.4s ease-out ${delay}s both` : 'none',
  }}>
    <span style={{ fontFamily: FONTS.sans, fontSize: 11, color: COLORS.textSecondary }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
      <span style={{ fontFamily: FONTS.sans, fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{value}</span>
      {unit && <span style={{ fontFamily: FONTS.sans, fontSize: 10, color: COLORS.textMuted }}>{unit}</span>}
      {trend && (
        <span style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 10, 
          fontWeight: 600,
          color: trend > 0 ? COLORS.positive : COLORS.negative,
          marginLeft: 4
        }}>
          {trend > 0 ? '↑' : '↓'}
        </span>
      )}
    </div>
  </div>
);

// ============ RISK TRIGGER CARD ============
const RiskTrigger = ({ name, threshold, impact, isAnimated, delay }) => (
  <div style={{
    padding: '8px 10px',
    backgroundColor: 'rgba(245, 158, 11, 0.08)',
    borderLeft: `3px solid ${COLORS.warning}`,
    borderRadius: '0 6px 6px 0',
    marginBottom: 8,
    opacity: isAnimated ? 1 : 0,
    animation: isAnimated ? `slide4FadeInUp 0.4s ease-out ${delay}s both` : 'none',
  }}>
    <div style={{ fontFamily: FONTS.sans, fontSize: 11, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 2 }}>
      {name}
    </div>
    <div style={{ display: 'flex', gap: 12 }}>
      <span style={{ fontFamily: FONTS.sans, fontSize: 9, color: COLORS.warning, fontWeight: 600 }}>
        Threshold: {threshold}
      </span>
      <span style={{ fontFamily: FONTS.sans, fontSize: 9, color: COLORS.textSecondary }}>
        → {impact}
      </span>
    </div>
  </div>
);

// ============ PRIORITY CARD ============
const PriorityCard = ({ number, title, isAnimated, delay }) => (
  <div style={{
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: '14px 16px',
    borderTop: `3px solid ${COLORS.gold}`,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    opacity: isAnimated ? 1 : 0,
    animation: isAnimated ? `slide4FadeInUp 0.4s ease-out ${delay}s both` : 'none',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'default',
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-3px)';
    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
  }}
  >
    <div style={{
      width: 26,
      height: 26,
      borderRadius: '50%',
      backgroundColor: COLORS.pageBg,
      color: COLORS.textWhite,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONTS.sans,
      fontSize: 12,
      fontWeight: 700,
      flexShrink: 0,
    }}>
      {number}
    </div>
    <span style={{
      fontFamily: FONTS.sans,
      fontSize: 12,
      fontWeight: 600,
      color: COLORS.textPrimary,
      lineHeight: 1.3,
    }}>
      {title}
    </span>
  </div>
);

// ============ MAIN SLIDE ============
const IncomeStatementSlide4 = ({ data, language = 'pt' }) => {
  const [isAnimated, setIsAnimated] = useState(false);
  
  useEffect(() => {
    injectAnimationStyles();
    const timer = setTimeout(() => setIsAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Default data for Cash Conversion
  const d = data || {
    ebitda: 30.1,
    workingCapital: -2.4,
    taxes: -1.8,
    otherAdjustments: -0.5,
    operatingCashFlow: 25.4,
    capex: -8.2,
    freeCashFlow: 17.2,
    netDebtEbitda: 1.2,
    debtCoverage: 3.8,
    availableCash: 12.5,
  };

  // Waterfall data
  const waterfallData = [
    { label: 'EBITDA', value: d.ebitda, isFirst: true },
    { label: 'Working Capital', value: d.workingCapital },
    { label: 'Impostos', value: d.taxes },
    { label: 'Outros ajustes', value: d.otherAdjustments },
    { label: 'FCO', value: d.operatingCashFlow, isSubtotal: true },
    { label: 'CAPEX', value: d.capex },
    { label: 'Free Cash Flow', value: d.freeCashFlow, isLast: true },
  ];

  // Capital metrics
  const capitalMetrics = language === 'pt' ? [
    { label: 'Fluxo de Caixa Operacional', value: `${d.operatingCashFlow.toFixed(1)}`, unit: 'USD M', trend: 1 },
    { label: 'CAPEX', value: `${Math.abs(d.capex).toFixed(1)}`, unit: 'USD M', trend: -1 },
    { label: 'Dívida Líquida / EBITDA', value: `${d.netDebtEbitda.toFixed(1)}`, unit: 'x', trend: -1 },
    { label: 'Cobertura da Dívida', value: `${d.debtCoverage.toFixed(1)}`, unit: 'x', trend: 1 },
    { label: 'Caixa Disponível', value: `${d.availableCash.toFixed(1)}`, unit: 'USD M', trend: 1 },
  ] : [
    { label: 'Operating Cash Flow', value: `${d.operatingCashFlow.toFixed(1)}`, unit: 'USD M', trend: 1 },
    { label: 'CAPEX', value: `${Math.abs(d.capex).toFixed(1)}`, unit: 'USD M', trend: -1 },
    { label: 'Net Debt / EBITDA', value: `${d.netDebtEbitda.toFixed(1)}`, unit: 'x', trend: -1 },
    { label: 'Debt Coverage', value: `${d.debtCoverage.toFixed(1)}`, unit: 'x', trend: 1 },
    { label: 'Available Cash', value: `${d.availableCash.toFixed(1)}`, unit: 'USD M', trend: 1 },
  ];

  // Risk triggers
  const riskTriggers = language === 'pt' ? [
    { name: 'Preço médio /ct', threshold: '-10%', impact: 'Reduz EBITDA em ~3M' },
    { name: 'Quilates vendidos', threshold: '-15%', impact: 'Pressiona conversão FCO' },
    { name: 'Timing operacional', threshold: '>5 dias atraso', impact: 'Afecta working capital' },
    { name: 'Custo combustível', threshold: '+20%', impact: 'Comprime margem 1.5pp' },
  ] : [
    { name: 'Average price /ct', threshold: '-10%', impact: 'Reduces EBITDA by ~3M' },
    { name: 'Carats sold', threshold: '-15%', impact: 'Pressures FCO conversion' },
    { name: 'Operational timing', threshold: '>5 days delay', impact: 'Affects working capital' },
    { name: 'Fuel cost', threshold: '+20%', impact: 'Compresses margin 1.5pp' },
  ];

  // Management priorities
  const priorities = language === 'pt' ? [
    'Proteger preço/mix comercial',
    'Preservar conversão em caixa',
    'Controlar execução e custo crítico',
  ] : [
    'Protect price/commercial mix',
    'Preserve cash conversion',
    'Control execution and critical cost',
  ];

  const editorialPhrase = language === 'pt'
    ? 'O EBITDA só cria valor financeiro quando se converte em caixa e preserva capacidade de investimento com conforto de balanço.'
    : 'EBITDA only creates financial value when it converts to cash and preserves investment capacity with balance sheet comfort.';

  return (
    <div style={{ 
      width: 1600, 
      height: 900, 
      backgroundColor: COLORS.pageBg, 
      position: 'relative', 
      fontFamily: FONTS.sans, 
      overflow: 'hidden' 
    }}>
      
      {/* ============ BLOCO 1: TÍTULO + SUBTÍTULO ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        top: 40, 
        right: 60,
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'slide4FadeIn 0.5s ease-out 0s both' : 'none',
      }}>
        <h1 style={{ 
          fontFamily: FONTS.serif, 
          fontSize: 30, 
          lineHeight: '38px', 
          fontWeight: 700, 
          color: COLORS.textWhite, 
          margin: 0,
        }}>
          {language === 'pt' ? 'Conversão em Caixa, Investimento e Risco' : 'Cash Conversion, Investment & Risk'}
        </h1>
        
        <p style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 13, 
          color: '#64748B', 
          marginTop: 10,
          marginBottom: 0
        }}>
          {language === 'pt'
            ? 'Leitura simplificada da conversão operacional em caixa, capacidade de investimento e gatilhos de risco'
            : 'Simplified reading of operational cash conversion, investment capacity and risk triggers'}
        </p>
      </div>

      {/* ============ BLOCO 2 & 3: CONTEÚDO PRINCIPAL ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        top: 130, 
        right: 60, 
        bottom: 130,
        display: 'flex',
        gap: 20
      }}>
        
        {/* LEFT: Waterfall de Caixa (dominante) */}
        <div style={{ 
          flex: 1.4,
          backgroundColor: COLORS.contentBg, 
          borderRadius: 14, 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          opacity: isAnimated ? 1 : 0,
          animation: isAnimated ? 'slide4FadeInUp 0.5s ease-out 0.1s both' : 'none',
        }}>
          <div style={{ padding: '18px 24px 10px 24px' }}>
            <span style={{ fontFamily: FONTS.serif, fontSize: 14, fontWeight: 600, color: COLORS.textPrimary }}>
              {language === 'pt' ? 'Ponte de Caixa do Trimestre' : 'Quarterly Cash Bridge'}
            </span>
          </div>
          <div style={{ flex: 1, padding: '0 24px 10px 24px' }}>
            <CashWaterfallChart data={waterfallData} height={380} isAnimated={isAnimated} />
          </div>
          <div style={{ 
            padding: '14px 24px 18px 24px',
            borderTop: '1px solid rgba(0,0,0,0.06)'
          }}>
            <p style={{
              fontFamily: FONTS.serif,
              fontSize: 13,
              fontStyle: 'italic',
              color: COLORS.textSecondary,
              margin: 0,
              lineHeight: 1.5
            }}>
              "{editorialPhrase}"
            </p>
          </div>
        </div>

        {/* RIGHT: Capital & Risk */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 16
        }}>
          
          {/* Capital Scorecard */}
          <div style={{ 
            flex: 1,
            backgroundColor: COLORS.contentBg, 
            borderRadius: 14, 
            padding: '16px 20px',
            opacity: isAnimated ? 1 : 0,
            animation: isAnimated ? 'slide4FadeInUp 0.5s ease-out 0.2s both' : 'none',
          }}>
            <div style={{ 
              fontFamily: FONTS.serif, 
              fontSize: 13, 
              fontWeight: 600, 
              color: COLORS.textPrimary,
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: `2px solid ${COLORS.gold}`
            }}>
              {language === 'pt' ? 'Scorecard de Capital' : 'Capital Scorecard'}
            </div>
            {capitalMetrics.map((metric, i) => (
              <CapitalMetric 
                key={i} 
                {...metric} 
                isAnimated={isAnimated} 
                delay={0.3 + i * 0.08} 
              />
            ))}
          </div>

          {/* Risk Triggers */}
          <div style={{ 
            flex: 1,
            backgroundColor: COLORS.contentBg, 
            borderRadius: 14, 
            padding: '16px 20px',
            opacity: isAnimated ? 1 : 0,
            animation: isAnimated ? 'slide4FadeInUp 0.5s ease-out 0.3s both' : 'none',
          }}>
            <div style={{ 
              fontFamily: FONTS.serif, 
              fontSize: 13, 
              fontWeight: 600, 
              color: COLORS.textPrimary,
              marginBottom: 10,
              paddingBottom: 8,
              borderBottom: `2px solid ${COLORS.warning}`
            }}>
              {language === 'pt' ? 'Gatilhos de Risco' : 'Risk Triggers'}
            </div>
            {riskTriggers.map((trigger, i) => (
              <RiskTrigger 
                key={i} 
                {...trigger} 
                isAnimated={isAnimated} 
                delay={0.5 + i * 0.08} 
              />
            ))}
          </div>
        </div>
      </div>

      {/* ============ BLOCO 4: 3 PRIORIDADES DE GESTÃO ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        bottom: 50, 
        right: 60,
        display: 'flex',
        gap: 16
      }}>
        {priorities.map((title, i) => (
          <PriorityCard
            key={i}
            number={i + 1}
            title={title}
            isAnimated={isAnimated}
            delay={0.8 + i * 0.1}
          />
        ))}
      </div>

      {/* ============ FOOTER ============ */}
      <div style={{ 
        position: 'absolute', 
        left: 60, 
        right: 60, 
        bottom: 14,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        opacity: isAnimated ? 1 : 0,
        animation: isAnimated ? 'slide4FadeIn 0.4s ease-out 1.2s both' : 'none',
      }}>
        <span style={{ fontFamily: FONTS.sans, fontSize: 9, color: '#64748B' }}>
          {language === 'pt' 
            ? 'ABC | Demonstração de Resultados Executiva | Versão final de apresentação'
            : 'ABC | Executive Income Statement | Final presentation version'}
        </span>
        <span style={{ 
          fontFamily: FONTS.sans, 
          fontSize: 9, 
          color: COLORS.gold, 
          fontWeight: 600,
          padding: '3px 10px',
          borderRadius: 4,
        }}>
          Board-ready
        </span>
        <span style={{ fontFamily: FONTS.sans, fontSize: 9, color: '#64748B' }}>4</span>
      </div>
    </div>
  );
};

export default IncomeStatementSlide4;
